'use client';

import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { useEffect, useState } from 'react';
import useScrollReveal from '@/hooks/useScrollReveal';
import { trackCTAClick } from '@/lib/analytics';

export default function HeroSection() {
  const [scrollY, setScrollY] = useState(0);
  const { ref: heroRef, isVisible: heroVisible } = useScrollReveal<HTMLDivElement>();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section ref={heroRef} id="hero" className="relative min-h-screen flex items-center justify-center bg-brand-plum text-white overflow-hidden">
      <div className="absolute inset-0 z-0">
        <AppImage
          src="https://images.unsplash.com/photo-1592916948341-4e999f4af1a6"
          alt="Street artist creating bold graffiti mural with raw urban energy and authentic creative spirit"
          width={1920}
          height={1080}
          className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
      </div>

      <div className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 flex items-center">
        <div className={`max-w-4xl py-16 sm:py-20 transition-all duration-1000 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-block px-3 py-1.5 sm:px-5 sm:py-2 bg-brand-teal text-white font-body text-[10px] sm:text-xs font-bold mb-4 sm:mb-6 uppercase tracking-wider shadow-lg transition-all duration-300 hover:bg-brand-green">
            Dallas-Based • Second-Chance Focused
          </div>
          <h1 className="font-headline text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-white leading-[1.1] font-bold mb-4 sm:mb-6 drop-shadow-2xl">
            You've Got the Talent. Let's Build the Infrastructure.
          </h1>
          <p className="font-body text-base sm:text-lg md:text-xl text-white/95 leading-relaxed mb-6 sm:mb-10 max-w-3xl">
            Here's the truth: most second-chance founders fail not because they lack skill, but because they lack the professional infrastructure to compete. We fix that. Brand strategy, websites, systems—the stuff that makes talent visible and businesses scalable. No charity. No shortcuts. Just the real tools you need to win contracts and build something that lasts.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Link
              href="/contact"
              onClick={() => trackCTAClick('Start the Conversation', 'Hero Section - Homepage')}
              className="group inline-block px-6 sm:px-8 py-3.5 sm:py-4 bg-brand-teal text-white font-cta font-bold text-sm sm:text-base hover:bg-brand-green transition-all duration-300 text-center transform hover:scale-105 hover:shadow-2xl shadow-lg active:scale-95 active:shadow-md min-h-[44px] flex items-center justify-center">
              
              <span className="flex items-center justify-center gap-2 sm:gap-3">
                Start the Conversation
                <span className="group-hover:translate-x-2 transition-transform text-base sm:text-lg">→</span>
              </span>
            </Link>
            <Link
              href="/portfolio"
              onClick={() => trackCTAClick('View Our Work', 'Hero Section - Homepage')}
              className="group inline-block px-6 sm:px-8 py-3.5 sm:py-4 border-2 border-white text-white font-cta font-bold text-sm sm:text-base hover:bg-white hover:text-brand-plum transition-all duration-300 text-center transform hover:scale-105 hover:shadow-2xl shadow-lg active:scale-95 active:shadow-md min-h-[44px] flex items-center justify-center">
              
              View Our Work
            </Link>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 sm:bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-5 h-8 sm:w-6 sm:h-10 border-2 border-white rounded-full flex items-start justify-center p-1.5 sm:p-2">
          <div className="w-1 h-2 sm:h-3 bg-white rounded-full animate-pulse"></div>
        </div>
      </div>
    </section>);

}